import { lastValueFrom } from 'rxjs';

import {
  getStorage, getStorageError, getStorageSuccess,
  getStorageForWB, getStorageForWBSuccess,
  getStorageHistory, getStorageHistorySuccess,
  getCartStorage, getCartStorageSuccess,
  updateCartStorage, updateCartStorageSuccess, updateCartStorageError,
  addItemToStorageWB, addItemToStorageWBOk,
  triggerDeleteStorageWB, deleteStorageWBSuccessThenResetFromStore,
  triggerInsertStorageWineboxDetailsIntoStore,
  setStorageWineBoxDetails, toggleShowWBItemsModal,
  triggerRemoveStorageWineBoxDetailsFromStore,
  showStorageProductDetailModal, toggleStorageProductDetailModal, hideStorageProductDetailModal,
  deleteCartStorage, clearLastStorageProductAddedToCart, deleteCartStorageError,
  getLastStorageOrder, getLastStorageOrderSuccess,
  showStorageFilterModal, toggleStorageFilterModal, hideStorageFilterModal,
  addStorageWineBoxToCart,
  getDashboardStats, setDashboardStats, resetCartStorage,
  sendSignatureContract
} from '../slices/storage.slice';

import {
  openWineboxModal, closeWineboxModal,
  hidePageProgress, showPageProgress,
  closeIncompatibleProductsModal
} from '../slices/gui.slice';

import { getCart } from '../slices/cart.slice';
import { changeStep } from '../slices/checkout.slice';
import { addApiErrorToast, addToast } from '../slices/toast.slice';
import { showAddBoxProductSuccess } from '../slices/box.slice';

import { AppListenerEntry } from '@/redux';
import { STEP_CHANGE } from '@/constants/checkout';

export const epicGetStorage: AppListenerEntry = {
  actionCreator: getStorage,
  effect: async ({ payload }: ReturnType<typeof getStorage>, { dispatch, extra: { storageService } }) => {
    try {
      const storage = await lastValueFrom(storageService.getStorage(payload.filter));
      dispatch(getStorageSuccess({ storage }));
    } catch (error) {
      dispatch(getStorageError({ error }));
    }
  }
};

export const epicGetStorageForWB: AppListenerEntry = {
  actionCreator: getStorageForWB,
  effect: async ({ payload }: ReturnType<typeof getStorageForWB>, { dispatch, extra: { storageService } }) => {
    try {
      const itemsList = await lastValueFrom(storageService.getStorageForWineBox(payload.filter));
      dispatch(getStorageForWBSuccess({ itemsList }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicGetStorageHistory: AppListenerEntry = {
  actionCreator: getStorageHistory,
  effect: async ({ payload }: ReturnType<typeof getStorageHistory>, { dispatch, extra: { storageService } }) => {
    try {
      const storageHistoryList: any[] = await lastValueFrom(storageService.getStorageHistory(payload.status));
      dispatch(getStorageHistorySuccess({ storageHistoryList }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicGetCartStorage: AppListenerEntry = {
  actionCreator: getCartStorage,
  effect: async (_action: ReturnType<typeof getCartStorage>, { dispatch, extra: { storageService } }) => {
    try {
      const cartStorage: any[] = await lastValueFrom(storageService.getCartStorage());
      dispatch(getCartStorageSuccess({ cartStorage }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicUpdateCartStorage: AppListenerEntry = {
  actionCreator: updateCartStorage,
  effect: async ({ payload }: ReturnType<typeof updateCartStorage>, { dispatch, extra: { storageService } }) => {
    try {
      const cartStorage: any[] = await lastValueFrom(storageService.updateCartStorage(payload.cartStorageItems));
      dispatch(updateCartStorageSuccess({
        lastStorageProductAddedToCart: payload.cartStorageItems[0],
        cartStorage,
      }));
    } catch (error) {
      dispatch(updateCartStorageError({ error }));
    }
  }
};

export const epicUpdateCartStorageSuccess: AppListenerEntry = {
  actionCreator: updateCartStorageSuccess,
  effect: async (_action: ReturnType<typeof updateCartStorageSuccess>, { dispatch }) => {
    dispatch(getStorage({ filter: undefined }));
  }
};

export const epicAddItemToStorageWB: AppListenerEntry = {
  actionCreator: addItemToStorageWB,
  effect: async ({ payload }: ReturnType<typeof addItemToStorageWB>, { getState, dispatch, extra: { storageService } }) => {
    const { storage } = getState();
    const firstElem = storage.storageWineBox.products.length > 0 ? false : true;
    const { itemId, quantity } = payload;
    try {
      const storageWineBox: any = await lastValueFrom(storageService.addItemToStorageWB(itemId, quantity));
      const isAdd = storage.storageWineBox.nbItems < storageWineBox.nbItem;
      const isFull = storageWineBox.complete;
      const isToOpen = firstElem || isFull;

      dispatch(addItemToStorageWBOk({ storageWineBox }));

      if (isAdd && !isFull) {
        dispatch(showAddBoxProductSuccess());
      }

      if (isToOpen) {
        dispatch(openWineboxModal());
      }
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicDeleteStorageWineBox: AppListenerEntry = {
  actionCreator: triggerDeleteStorageWB,
  effect: async ({ payload }: ReturnType<typeof triggerDeleteStorageWB>, { dispatch, extra: { storageService } }) => {
    try {
      await lastValueFrom(storageService.deleteStorageWB(payload.wineBoxStorageId));
      dispatch(deleteStorageWBSuccessThenResetFromStore());
      dispatch(closeWineboxModal());
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicInsertStorageWineboxDetailsIntoStore: AppListenerEntry = {
  actionCreator: triggerInsertStorageWineboxDetailsIntoStore,
  effect: ({ payload }: ReturnType<typeof triggerInsertStorageWineboxDetailsIntoStore>, { dispatch }) => {
    dispatch(setStorageWineBoxDetails({ wbItems: payload.winebox?.storageItems }));
    dispatch(toggleShowWBItemsModal({ show: true }));
  }
};

export const epicRemoveStorageWineboxDetailsFromStore: AppListenerEntry = {
  actionCreator: triggerRemoveStorageWineBoxDetailsFromStore,
  effect: (_action: ReturnType<typeof triggerRemoveStorageWineBoxDetailsFromStore>, { dispatch }) => {
    dispatch(setStorageWineBoxDetails({ wbItems: [] }));
    dispatch(toggleShowWBItemsModal({ show: false }));
  }
};

export const epicShowStorageProductDetailModal: AppListenerEntry = {
  actionCreator: showStorageProductDetailModal,
  effect: ({ payload }: ReturnType<typeof showStorageProductDetailModal>, { dispatch }) => {
    const { storageProduct, fromWineBox } = payload;
    dispatch(toggleStorageProductDetailModal({ show: true, storageProduct, fromWineBox }));
  }
};

export const epicHideStorageProductDetailModal: AppListenerEntry = {
  actionCreator: hideStorageProductDetailModal,
  effect: (_action: ReturnType<typeof hideStorageProductDetailModal>, { dispatch }) => {
    dispatch(toggleStorageProductDetailModal({ show: false, storageProduct: {}, fromWineBox: false }));
  }
};

export const epicDeleteCartStorage: AppListenerEntry = {
  actionCreator: deleteCartStorage,
  effect: async ({ payload }: ReturnType<typeof deleteCartStorage>, { dispatch, extra: { storageService } }) => {
    try {
      const cartStorage: any[] = await lastValueFrom(storageService.updateCartStorage(payload.cartStorageItems));
      dispatch(clearLastStorageProductAddedToCart());
      dispatch(updateCartStorageSuccess({ cartStorage }));
    } catch (error) {
      dispatch(deleteCartStorageError({ error }));
    }
  }
};

export const epicGetLastStorageOrder: AppListenerEntry = {
  actionCreator: getLastStorageOrder,
  effect: async ({ payload }: ReturnType<typeof getLastStorageOrder>, { dispatch, extra: { storageService } }) => {
    try {
      const lastStorageOrder = await lastValueFrom(storageService.getStorageOrderByOrderId(payload.orderId));
      dispatch(getLastStorageOrderSuccess({ lastStorageOrder }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicShowStorageFilterModal: AppListenerEntry = {
  actionCreator: showStorageFilterModal,
  effect: (_action: ReturnType<typeof showStorageFilterModal>, { dispatch }) => {
    dispatch(toggleStorageFilterModal({ show: true }));
  }
};

export const epicHideStorageFilterModal: AppListenerEntry = {
  actionCreator: hideStorageFilterModal,
  effect: (_action: ReturnType<typeof hideStorageFilterModal>, { dispatch }) => {
    dispatch(toggleStorageFilterModal({ show: false }));
  }
};

export const epicAddStorageWineBoxToCart: AppListenerEntry = {
  actionCreator: addStorageWineBoxToCart,
  effect: async ({ payload }: ReturnType<typeof addStorageWineBoxToCart>, { dispatch, extra: { storageService } }) => {
    try {
      dispatch(showPageProgress());
      const cartStorage: any[] = await lastValueFrom(storageService.addStorageWineBoxToCart(payload.wineBoxStorageId));
      dispatch(clearLastStorageProductAddedToCart());
      dispatch(updateCartStorageSuccess({ cartStorage }));
      dispatch(deleteStorageWBSuccessThenResetFromStore());
    } catch (error) {
      dispatch((addApiErrorToast({ error })));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};

export const epicGetDashboardStats: AppListenerEntry = {
  actionCreator: getDashboardStats,
  effect: async (_action: ReturnType<typeof getDashboardStats>, { dispatch, extra: { storageService } }) => {
    try {
      dispatch(showPageProgress());
      const stats = await lastValueFrom(storageService.getDashboardStats());
      dispatch(setDashboardStats({ stats }));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};

export const epicResetCartStorage: AppListenerEntry = {
  actionCreator: resetCartStorage,
  effect: async (_action: ReturnType<typeof getDashboardStats>, { dispatch, extra: { storageService } }) => {
    try {
      await lastValueFrom(storageService.resetCartStorage());
      dispatch(getCart());
      dispatch(getCartStorage());
      dispatch(changeStep({ next: STEP_CHANGE.NEXT }));
      dispatch(closeIncompatibleProductsModal());
    } catch (error) {
      dispatch(addApiErrorToast(error));
    }
  }
};

export const epicSendSignatureContract: AppListenerEntry = {
  actionCreator: sendSignatureContract,
  effect: async ({ payload }: ReturnType<typeof sendSignatureContract>, { dispatch, extra: { storageService } }) => {
    const { pickId, t } = payload;
    try {
      dispatch(showPageProgress());
      await storageService.sendSignatureContract([pickId]);
      dispatch(addToast({ msg: t('admin:contractSent'), err: false }));
    } catch (error) {
      dispatch(addApiErrorToast(error));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};
